import { useEffect, useState } from "react"
import { Button } from "@components/common/button"
import { Card, CardContent } from "@components/common/card"
import {
  ALL_ESTIMATE_BOQ_ENDPOINT,
  DELETE_ESTIMATE_BOQ_ENDPOINT,
  FINANCIAL_SERVER_URL,
  RESTORE_ESTIMATE_BOQ_ENDPOINT,
} from "@pma/utils/api-routes"
import axios from "axios"
import { useAuth } from "@pma/stores/auth-store"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { useMutation, useQuery } from "@tanstack/react-query"
import { DictType } from "@pma/types"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import useTranslator from "@pma/hooks/use-translator"
import CreateNewBoQEstimate from "./create-new-boq-estimate"
import PmsLoader from "@components/common/loader"
import { EstimateBoQDetails } from "./estimate-boq-details"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/common/select"
import { CornerLeftDown, Trash } from "lucide-react"
import { Separator } from "@components/common/separator"
import { toast } from "@pma/hooks/use-toast"
import { Switch } from "../common/switch"
import { cn, getFinancialErrorMessage } from "@pma/utils/helpers"

interface Props {
  projectView?: boolean
  activeEstimate?: number
}
export function EstimateBoqsTable({}: Props) {
  const [searchQuery, setSearchQuery] = useState("")
  const { translate } = useTranslator()

  const { authConfig } = useAuth()
  const [searchParam] = useSearchParams()
  const navigate = useNavigate()
  const projectId = useParams().projectId

  const [showDeleted, setShowDeleted] = useState(false)

  const accessToken = parseCookies()[ACCESS_TOKEN]

  const orgId = authConfig?.last_organization_id

  const newParams = new URLSearchParams(searchParam)

  const activeBoQ = searchParam.get("estimate")

  const allStateTabs = authConfig?.tabs ?? {}
  const mainBoQActiveTab = allStateTabs ? allStateTabs["boq"] : undefined
  const activeTabs: DictType = mainBoQActiveTab || {
    main: "estimates",
    sub: undefined,
  }

  const isOrganizationOwner = authConfig?.workspace?.owner_id === authConfig?.id
  const isAdmin = authConfig?.companies_of_user?.find((company: any) => {
    if (company?.id === authConfig?.last_organization_id && company?.role?.id == 22 /* ADMIN_ROLE_ID */) return true
  })

  const getEstimateBoQs = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + ALL_ESTIMATE_BOQ_ENDPOINT,
      {
        organization_id: orgId,
        show_deleted: showDeleted,
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,

          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["estimate-boq", orgId, showDeleted],
    queryFn: getEstimateBoQs,
  })

  const estimateBoQs = data || []

  const filteredEstimateBoqs = estimateBoQs?.filter((boq: DictType) =>
    boq.name.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  const setActiveBoQId = (boqId: string) => {
    newParams.delete("main")
    newParams.set("estimate", boqId)
    navigate(`?${newParams.toString()}`)
  }

  const deleteBoQEstimate = async (boqId: string) => {
    const response = await axios.delete(
      FINANCIAL_SERVER_URL + DELETE_ESTIMATE_BOQ_ENDPOINT + `/${boqId}`,
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const handleDeleteBoqMutation = useMutation({
    mutationKey: ["delete-boq"],
    mutationFn: deleteBoQEstimate,
    onSuccess: () => {
      refetch()
      toast({
        title: translate("Success"),
        description: translate("BoQ Deleted Successfully"),
        variant: "success",
      })

      return navigate(`?${newParams.toString()}`)
    },
    onError: (error) => {
      toast({
        title: translate("Error"),
        description: getFinancialErrorMessage(error, "delete estimate", translate),
        variant: "destructive",
      })
    },
  })

  const restoreBoQEstimate = async (boqId: string) => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + RESTORE_ESTIMATE_BOQ_ENDPOINT,
      {
        organization_id: orgId,
        boqs_id: [boqId],
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const restoreBoqMutation = useMutation({
    mutationKey: ["restore-boq"],
    mutationFn: restoreBoQEstimate,
    onSuccess: () => {
      refetch()
      toast({
        title: translate("Success"),
        description: translate("BoQ restore Successfully"),
        variant: "success",
      })

      return navigate(`?${newParams.toString()}`)
    },
    onError: (error) => {
      toast({
        title: translate("Error"),
        description: getFinancialErrorMessage(error, "delete estimate", translate),
        variant: "destructive",
      })
    },
  })

  useEffect(() => {
    if (activeTabs?.main === "estimates") {
      newParams.delete("main")
    }
    if (filteredEstimateBoqs && !activeBoQ && activeTabs?.main === "estimates") {
      filteredEstimateBoqs[0]?.id && setActiveBoQId(filteredEstimateBoqs[0]?.id)
    }
  }, [filteredEstimateBoqs, activeTabs?.main, activeBoQ])

  return (
    <>
      <Card>
        <CardContent className='p-2'>
          <div className='mb-4 flex items-center justify-between'>
            {filteredEstimateBoqs?.length > 0 && (
              <Select value={activeBoQ || undefined} onValueChange={setActiveBoQId}>
                <SelectTrigger
                  className='h-7 w-[160px] truncate text-ellipsis rounded-md border-none bg-primary capitalize text-white'
                  aria-label='Select a value'
                >
                  <SelectValue
                    defaultValue={activeBoQ || undefined}
                    className='capitalize'
                    placeholder={translate("Estimate BoQ")}
                  />
                </SelectTrigger>
                <SelectContent className='gap-y-2 rounded-xl pb-2'>
                  {filteredEstimateBoqs?.map((boq: DictType, index: number) => (
                    <div className='flex items-center gap-2 p-0.5' key={index}>
                      <SelectItem
                        value={String(boq?.id)}
                        className={cn("rounded-lg", boq.deleted ? "text-red-500" : "")}
                      >
                        {translate(boq?.name)}
                      </SelectItem>
                      {boq.deleted &&
                      (isOrganizationOwner || isAdmin || boq.deleted_by == authConfig.id) ? (
                        <Button
                          size='icon'
                          variant={"ghost"}
                          disabled={restoreBoqMutation.isPending}
                          onClick={() => restoreBoqMutation.mutate(boq?.id)}
                          className='text-primary'
                        >
                          <CornerLeftDown size={14} />
                        </Button>
                      ) : (
                        !boq.deleted ?
                        <Button
                          size='icon'
                          variant={"ghost"}
                          disabled={handleDeleteBoqMutation.isPending}
                          onClick={() => handleDeleteBoqMutation.mutate(boq?.id)}
                          className='text-destructive'
                        >
                          <Trash size={14} />
                        </Button> : null
                      )}
                    </div>
                  ))}
                  <div className='mt-1'>
                    <Separator />
                  </div>
                </SelectContent>
              </Select>
            )}

            <div className='flex gap-2'>
              <div className='flex items-center'>
                <div className='flex items-center gap-2'>
                  <Switch
                    checked={showDeleted}
                    className='h-6 w-11'
                    onCheckedChange={(checked) => setShowDeleted(checked)}
                  />
                  <h4 className='text-sm font-semibold'>
                    {translate("Show Deleted Estimate Template")}
                  </h4>
                </div>
              </div>

              <CreateNewBoQEstimate downloadTpl projectId={projectId} />
            </div>
          </div>

          {isLoading && <PmsLoader />}
          {filteredEstimateBoqs?.length < 1 && !isLoading && (
            <div className='flex w-full items-center justify-center py-4 text-sm font-medium'>
              <p>{translate("No estimates found")}</p>
            </div>
          )}

          {activeBoQ && filteredEstimateBoqs?.length > 0 && (
            <div className='py-4'>
              <EstimateBoQDetails boqId={activeBoQ} />
            </div>
          )}
        </CardContent>
      </Card>
    </>
  )
}
